import { Form } from '@ant-design/compatible';
import { Checkbox, Col, Input, Radio, Row } from 'antd';
import ErrorHandler from 'common/error/components/error-handler';
import SidePanel from 'common/side-panel/side-panel';
import Spinner from 'common/spinner/components/spinner';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Button, Div, Icon as FAIcon, Flex, FormItem } from 'ui';
import { withRouter } from 'utils/withRouter';
import Table from '../../../common/table/table';
import { viewTypes } from '../../../utils/app-configs';

export const Divider = styled(Div)`
  border-bottom: 1px solid ${p => p.theme.colors.border};
  margin: 0.5rem 0;
`;

const Buttons = styled(Flex)`
  > :first-child {
    margin-right: 20px;
  }
`;

const StyledButton = styled(Button)`
  text-transform: uppercase;
`;

const StyledTitle = styled.p`
  margin: 20px 0;
  font-size: 16px;
`;

const StyledForm = styled(Form)`
  margin-left: 20px;
`;

const StyledCol = styled(Col)`
  width: 400px;
  margin: 10px 0;
`;

const StyledContainer = styled.div`
  margin: 30px 0;
`;

const StyledRadio = styled(Radio)`
  .ant-radio-inner,
  .ant-radio-input {
    width: 25px;
    height: 25px;
  }
  margin-left: 8px;
  margin-top: 4px;
  .ant-radio-inner::after {
    width: 33px;
    height: 33px;
    top: 3px;
    left: 3px;
    border-radius: 50%;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  .ant-checkbox-inner,
  .ant-checkbox-input {
    width: 25px;
    height: 25px;
  }
  margin-left: 8px;
  margin-top: 4px;
  .ant-checkbox-inner::after {
    width: 10px;
    height: 10px;
    top: 12px;
    left: 6px;
  }
`;

const ButtonCol = styled(StyledCol)`
  display: ${p => p.isNone ? 'none' : 'flex'}
  button {
    width: 25px !important;
    height: 25px !important;
    border-radius: 50%;
  }
`;

const RemoveButton = styled(Button)`
  background-color: ${p => p.theme.colors.theme};
  box-shadow: none !important;
  padding: 1px;
  margin-left: 8px;
  margin-top: 4px;
  width: 32px !important;
  transition: none;
  i {
    cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  }
  &:hover {
    background-color: ${p => p.theme.colors.darkTheme};
  }
  
`;

const StyledFAIcon = styled(FAIcon)`
  color: white;
`;

const NoDefaultText = styled.i`
  color: ${p => p.theme.colors.textLight};
  font-size: 14px;
  white-space: pre-wrap;
`;

const StyledRadioGroup = styled(Radio.Group)`
  width: 100%;
`;

const StyledInputCol = styled(StyledCol)`
  height: 100%;
  ${p => p.isNone && 'text-align: right'}
`;

const ManageViews = ({
  intl,
  history,
  location,
  id,
  views = [],
  isSaving,
  isLoadingViews,
  form: { getFieldDecorator, getFieldValue, getFieldsError },
  updateViews,
  error,
  isPlanningBoard = false,
  isGantt = false,
  saveSuccess,
  expandOption = false,
}) => {
  const [customViews, setCustomViews] = useState([]);

  const cancel = () => {
    // find /manage-views or /manage-gantt-views
    const regex = /\/manage(-gantt)?-views/;
    if (regex.test(location.pathname)) {
      const goBackPath = location.pathname.replace(regex, '');

      history.replace(goBackPath);
    }
  };

  useEffect(() => {
    if (saveSuccess) {
      cancel();
    }
  }, [saveSuccess]);

  // use useEffect to prevent crash when refresing url and views are []
  useEffect(() => {
    setCustomViews(views?.filter(v =>
      (v.editable && v.templateType === viewTypes.user) || v.isNone)
      || [],
    );
  }, [views?.length]);

  const updateNewViews = () => {
    const editedViews = customViews?.filter(v => !v.isNone)?.map(v => ({
      ...v,
      isDefault: v.id === getFieldValue('defaultId'),
      isExpanded: getFieldValue(`expanded_${v.id}`),
      templateName: getFieldValue(`templateName_${v.id}`),
      sort: v.index,
    }));
    updateViews({ id, views: editedViews });
  };

  const deleteView = (v) => {
    const updatedViews = customViews?.filter(view => view.id !== v.id);
    setCustomViews(updatedViews);
  };

  const disabled = isSaving || isLoadingViews;
  getFieldDecorator('views', {
    initialValue: views,
  });

  const sort = (a, b) => {
    if (!a.isHidden && b.isHidden) {
      return -1;
    } else if (a.isHidden && !b.isHidden) {
      return 1;
    } else if ((!a.isHidden && !b.isHidden) || (a.isHidden && b.isHidden)) {
      return a.index - b.index;
    }
    return a.templateName?.localeCompare(b.templateName);
  };

  const handleDrop = (newOrderViews) => {
    const list = [
      ...newOrderViews,
    ]?.sort(sort);
    setCustomViews(list);
  };

  const getColumns = () => {
    const { formatMessage: translate } = intl;

    const columns = [
      {
        id: 'templateName',
        displayName: translate({ id: 'worklist.filters.TITLE_LABEL' }),
        fieldName: 'templateName',
        width: '400px',
        renderFn: v => <StyledInputCol isNone={v.isNone}>
          {v.isNone ?
            <NoDefaultText>
              {intl.formatMessage({ id: 'worklist.filters.NO_DEFAULT' })}
            </NoDefaultText> :
            <FormItem>
              {getFieldDecorator(`templateName_${v.id}`, {
                initialValue: v.templateName,
                rules: [{
                  required: true,
                  message: intl.formatMessage({ id: 'rules.VIEW_NAME' }),
                }],
              })(
                <Input
                  placeholder={intl.formatMessage({
                    id: 'worklist.filters.SAVE_PLACEHOLDER',
                  })}
                  disabled={disabled}
                />,
              )}
            </FormItem>
          }
        </StyledInputCol>,
      },
      {
        id: 'isDefault',
        displayName: translate({ id: 'worklist.filters.DEFAULT_LABEL' }),
        fieldName: 'isDefault',
        width: '60px',
        renderFn: v => <StyledCol>
          <StyledRadio
            value={v.id}
          />
        </StyledCol>,
      },
      {
        id: 'isExpanded',
        displayName: translate({ id: 'worklist.filters.EXPAND_LABEL' }),
        fieldName: 'isExpanded',
        width: '60px',
        renderFn: v => <>
          {!v.isNone && expandOption &&
            <StyledCol>
              {getFieldDecorator(`expanded_${v.id}`, {
                initialValue: !!v.isExpanded,
                valuePropName: 'checked',
              })(<StyledCheckbox />)}
            </StyledCol>
          }
        </>,
      },
      {
        id: 'actions',
        displayName: translate({ id: 'worklist.filters.DELETE_LABEL' }),
        fieldName: '',
        width: '50px',
        renderFn: v => <ButtonCol span={2} isNone={v.isNone}>
          {!v.isNone &&
            <RemoveButton
              onClick={() => deleteView(v)}
              disabled={disabled}
            >
              <StyledFAIcon type='trash-alt' light />
            </RemoveButton>
          }
        </ButtonCol>,
      },
    ];

    if (!expandOption) {
      return columns?.filter(item => item.id !== 'isExpanded');
    }

    return columns;
  };

  const renderViews = () => {
    if (isLoadingViews) {
      return <Spinner title={intl.formatMessage({ id: 'common.LOADING_VIEWS' })} />;
    }

    return customViews.length ?
      <Table
        lightHeader={true}
        movableRows={true}
        onDrop={handleDrop}
        columns={getColumns()}
        data={customViews?.map((v, i) => {
          let adjectedView = {
            ...v,
            disableMove: !!v.isNone,
          };
          // make sure that the none view has no index, so it can be sorted
          if (!v.isNone) {
            adjectedView = {
              ...adjectedView,
              index: i,
            };
          }
          return adjectedView;
        }) || []
        }
      /> :
      <FormattedMessage id='worklist.filters.NO_VIEWS' />;
  };

  const validationErrors = getFieldsError();
  const isInvalid = Object.values(validationErrors)?.filter(e => e).length;

  const defaultValidator = getFieldDecorator('defaultId', {
    initialValue: customViews?.find(v => v.isDefault)?.id || customViews?.find(v => v.isNone)?.id,
  });

  return (
    <SidePanel
      width={'40%'}
      title={isPlanningBoard ?
        intl.formatMessage(
          {
            id:
              'planningBoard.MANAGE_VIEWS_TITLE',
          },
          {
            type: isGantt ?
              intl.formatMessage({ id: 'planningBoard.GANTT' }) :
              intl.formatMessage({ id: 'planningBoard.WORKLIST' }),
          },
        ) :
        intl.formatMessage(
          {
            id: 'worklist.filters.MANAGE_VIEWS_TITLE',
          },
        )
      }
      onPanelClose={cancel}
    >
      <ErrorHandler type='notification' error={error} />
      <StyledForm layout='vertical'>
        <StyledTitle><FormattedMessage id='worklist.filters.MANAGE_INSTRUCTIONS' /></StyledTitle>
        <StyledContainer>
          <Row>
            {defaultValidator(
              <StyledRadioGroup>
                {renderViews()}
              </StyledRadioGroup>,
            )}
          </Row>
        </StyledContainer>
        <Buttons>
          <StyledButton onClick={cancel}>
            <FormattedMessage id='worklist.filters.CANCEL' />
          </StyledButton>
          <StyledButton
            type='secondary'
            onClick={() => updateNewViews()}
            disabled={disabled || isInvalid}
            loading={isSaving || isLoadingViews}
          >
            <FormattedMessage id='worklist.filters.SAVE_VIEWS_BUTTON' />
          </StyledButton>
        </Buttons>
      </StyledForm>
    </SidePanel>
  );
};

ManageViews.propTypes = {
  intl: PropTypes.object,
  id: PropTypes.string,
  views: PropTypes.array,
  history: PropTypes.object,
  location: PropTypes.object,
  isSaving: PropTypes.bool,
  isLoadingViews: PropTypes.bool,
  form: PropTypes.shape({
    getFieldDecorator: PropTypes.func,
    getFieldValue: PropTypes.func,
    setFieldsValue: PropTypes.func,
    getFieldsError: PropTypes.func,
  }),
  updateViews: PropTypes.func,
  error: PropTypes.bool,
  isPlanningBoard: PropTypes.bool,
  isGantt: PropTypes.bool,
  saveSuccess: PropTypes.bool,
  expandOption: PropTypes.bool,
};

export default Form.create()(withRouter(injectIntl(ManageViews)));
